// Per-step config cards for the action editor. Steps are arranged in a
// vertical list; each card matches its step kind. The recursive bit is
// CallApiStep, which has nested on_success and on_error step lists — those
// re-use this same StepList component.

import { useState } from 'react'
import { Plus, Trash2, ChevronDown, ChevronRight } from 'lucide-react'
import { FIELD_LABEL, INPUT } from '../blockEditors'
import type {
  ActionStep, CallApiStep, HttpMethod, PageInput,
} from '../types'
import { RefreshSourceStepEditor, TxnActionStepEditor, TxnUpdateStepEditor, type TxnStepCtx } from './TxnStepEditors'

interface StepEditorCtx extends TxnStepCtx {
  inputs: PageInput[]            // for name pickers in validate / focus_input / set_input / set_field_error / clear_inputs
  // Active IOFlow API names from the project's Integration design — when a
  // call_api step picks one, the call routes through the server-side
  // /integration/invoke proxy instead of a raw URL.
  ioflowApis?: string[]
}

const STEP_LABEL: Record<ActionStep['kind'], string> = {
  validate:        'Validate inputs',
  set_input:       'Set input value',
  clear_inputs:    'Clear inputs',
  set_field_error: 'Set field error',
  focus_input:     'Focus input',
  show_message:    'Show toast message',
  call_api:        'Call API',
  txn_action:      'Run a transaction action',
  txn_update:      'Update a record',
  refresh_source:  'Refresh data',
}

function makeStep(kind: ActionStep['kind']): ActionStep {
  switch (kind) {
    case 'validate':        return { kind, on_fail_focus: true }
    case 'set_input':       return { kind, name: '', value: '' }
    case 'clear_inputs':    return { kind }
    case 'set_field_error': return { kind, name: '', message: '' }
    case 'focus_input':     return { kind, name: '' }
    case 'show_message':    return { kind, level: 'success', text: '' }
    case 'call_api':        return { kind, method: 'POST', url: '/api/' }
    case 'txn_action':      return { kind, obj_name: '', action: '', ids: '{{item._txn_id}}' }
    case 'txn_update':      return { kind, obj_name: '', id: '{{item._txn_id}}', values: {} }
    case 'refresh_source':  return { kind }
  }
}

// on_success / on_error branches, shared by call_api and the record steps.
function Branches<S extends { on_success?: ActionStep[]; on_error?: ActionStep[] }>({
  step, upd, ctx,
}: { step: S; upd: (p: Partial<S>) => void; ctx: StepEditorCtx }) {
  return (
    <>
      <div style={{ marginTop: 4, paddingTop: 8, borderTop: '1px dashed var(--color-border)' }}>
        <StepList
          label="On success"
          steps={step.on_success || []}
          onChange={(next) => upd({ on_success: next.length ? next : undefined } as Partial<S>)}
          ctx={ctx}
        />
      </div>
      <div style={{ paddingTop: 8, borderTop: '1px dashed var(--color-border)' }}>
        <StepList
          label="On error"
          steps={step.on_error || []}
          onChange={(next) => upd({ on_error: next.length ? next : undefined } as Partial<S>)}
          ctx={ctx}
        />
      </div>
    </>
  )
}

const SUBSECTION: React.CSSProperties = {
  padding: 10, borderRadius: 8,
  background: 'var(--color-bg)',
  border: '1px solid var(--color-border)',
  display: 'flex', flexDirection: 'column', gap: 8,
}

const TWO_COL: React.CSSProperties = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }

// ─── Recursive list ───────────────────────────────────

export function StepList({
  steps, onChange, ctx, label,
}: {
  steps: ActionStep[]
  onChange: (next: ActionStep[]) => void
  ctx: StepEditorCtx
  label?: string
}) {
  const append = (kind: ActionStep['kind']) => onChange([...steps, makeStep(kind)])
  const update = (i: number, next: ActionStep) => {
    const out = [...steps]; out[i] = next; onChange(out)
  }
  const remove = (i: number) => onChange(steps.filter((_, k) => k !== i))

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {label && <label style={FIELD_LABEL}>{label}</label>}
      {steps.map((s, i) => (
        <StepCard
          key={i} step={s} ctx={ctx}
          onChange={(next) => update(i, next)}
          onRemove={() => remove(i)}
        />
      ))}
      <AddStepMenu onPick={append} />
    </div>
  )
}

function AddStepMenu({ onPick }: { onPick: (k: ActionStep['kind']) => void }) {
  const [open, setOpen] = useState(false)
  const all = Object.keys(STEP_LABEL) as ActionStep['kind'][]
  return (
    <div style={{ position: 'relative', alignSelf: 'flex-start' }}>
      <button
        onClick={(e) => { e.stopPropagation(); setOpen((o) => !o) }}
        className="btn btn-ghost btn-sm"
      >
        <Plus size={13} /> Add step
      </button>
      {open && (
        <div
          onMouseLeave={() => setOpen(false)}
          style={{
            position: 'absolute', top: '100%', left: 0, marginTop: 4, zIndex: 10,
            background: 'var(--color-bg-secondary)',
            border: '1px solid var(--color-border)',
            borderRadius: 6, boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
            minWidth: 200, padding: 4,
          }}
        >
          {all.map((k) => (
            <button
              key={k}
              onClick={(e) => { e.stopPropagation(); onPick(k); setOpen(false) }}
              style={{
                display: 'block', width: '100%', padding: '6px 10px', textAlign: 'left',
                background: 'transparent', border: 'none', borderRadius: 4,
                cursor: 'pointer', fontSize: '0.9125rem', color: 'var(--color-text)',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--color-accent-weak)')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
            >{STEP_LABEL[k]}</button>
          ))}
        </div>
      )}
    </div>
  )
}

// ─── Individual step card ─────────────────────────────

function StepCard({
  step, ctx, onChange, onRemove,
}: {
  step: ActionStep
  ctx: StepEditorCtx
  onChange: (next: ActionStep) => void
  onRemove: () => void
}) {
  const [collapsed, setCollapsed] = useState(false)
  return (
    <div style={SUBSECTION}>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        cursor: 'pointer', userSelect: 'none',
      }} onClick={() => setCollapsed(!collapsed)}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.9125rem', fontWeight: 600 }}>
          {collapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
          <span>{STEP_LABEL[step.kind]}</span>
        </div>
        <button
          onClick={(e) => { e.stopPropagation(); onRemove() }}
          className="btn btn-ghost btn-sm"
          style={{ padding: 2, color: 'var(--color-error)' }}
          title="Remove step"
        >
          <Trash2 size={13} />
        </button>
      </div>
      {!collapsed && <StepBody step={step} onChange={onChange} ctx={ctx} />}
    </div>
  )
}

function InputNamePicker({ value, inputs, onChange, placeholder = 'input name' }: {
  value: string; inputs: PageInput[]; onChange: (v: string) => void; placeholder?: string
}) {
  if (inputs.length === 0) {
    return (
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        style={{ ...INPUT, fontFamily: 'monospace' }}
      />
    )
  }
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} style={INPUT}>
      <option value="">— Select —</option>
      {inputs.map((i) => (
        <option key={i.name} value={i.name}>{i.label || i.name}</option>
      ))}
    </select>
  )
}

function StepBody({ step, ctx, onChange }: {
  step: ActionStep
  ctx: StepEditorCtx
  onChange: (next: ActionStep) => void
}) {
  switch (step.kind) {
    case 'validate':
      return (
        <>
          <NamesMultiPicker
            label="Inputs to validate (empty = all)"
            value={step.names || []}
            inputs={ctx.inputs}
            onChange={(names) => onChange({ ...step, names })}
          />
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.8625rem', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={step.on_fail_focus !== false}
              onChange={(e) => onChange({ ...step, on_fail_focus: e.target.checked })}
            />
            Focus the first failing input
          </label>
        </>
      )
    case 'set_input':
      return (
        <>
          <div>
            <label style={FIELD_LABEL}>Input</label>
            <InputNamePicker value={step.name} inputs={ctx.inputs} onChange={(name) => onChange({ ...step, name })} />
          </div>
          <div>
            <label style={FIELD_LABEL}>Value (supports {'{{vars}}'})</label>
            <input value={step.value} onChange={(e) => onChange({ ...step, value: e.target.value })} style={INPUT} />
          </div>
        </>
      )
    case 'clear_inputs':
      return (
        <NamesMultiPicker
          label="Inputs to clear (empty = all)"
          value={step.names || []}
          inputs={ctx.inputs}
          onChange={(names) => onChange({ ...step, names: names.length ? names : undefined })}
        />
      )
    case 'set_field_error':
      return (
        <>
          <div>
            <label style={FIELD_LABEL}>Input</label>
            <InputNamePicker value={step.name} inputs={ctx.inputs} onChange={(name) => onChange({ ...step, name })} />
          </div>
          <div>
            <label style={FIELD_LABEL}>Message (supports {'{{vars}}'})</label>
            <input value={step.message} onChange={(e) => onChange({ ...step, message: e.target.value })} style={INPUT} />
          </div>
        </>
      )
    case 'focus_input':
      return (
        <div>
          <label style={FIELD_LABEL}>Input</label>
          <InputNamePicker value={step.name} inputs={ctx.inputs} onChange={(name) => onChange({ ...step, name })} />
        </div>
      )
    case 'show_message':
      return (
        <>
          <div style={TWO_COL}>
            <div>
              <label style={FIELD_LABEL}>Level</label>
              <select
                value={step.level}
                onChange={(e) => onChange({ ...step, level: e.target.value as 'success' | 'error' | 'info' })}
                style={INPUT}
              >
                <option value="success">Success</option>
                <option value="error">Error</option>
                <option value="info">Info</option>
              </select>
            </div>
          </div>
          <div>
            <label style={FIELD_LABEL}>Message (supports {'{{vars}}'})</label>
            <input value={step.text} onChange={(e) => onChange({ ...step, text: e.target.value })} style={INPUT} />
          </div>
        </>
      )
    case 'call_api':
      return <CallApiStepEditor step={step} ctx={ctx} onChange={onChange} />
    case 'txn_action':
      return (
        <TxnActionStepEditor step={step} ctx={ctx} onChange={onChange}
          renderBranches={(s, upd) => <Branches step={s} upd={upd} ctx={ctx} />} />
      )
    case 'txn_update':
      return (
        <TxnUpdateStepEditor step={step} ctx={ctx} onChange={onChange}
          renderBranches={(s, upd) => <Branches step={s} upd={upd} ctx={ctx} />} />
      )
    case 'refresh_source':
      return <RefreshSourceStepEditor step={step} ctx={ctx} onChange={onChange} />
  }
}

// ─── call_api editor ─────────────────────────────────

function CallApiStepEditor({
  step, ctx, onChange,
}: {
  step: CallApiStep
  ctx: StepEditorCtx
  onChange: (next: CallApiStep) => void
}) {
  const upd = (patch: Partial<CallApiStep>) => onChange({ ...step, ...patch })
  const headers = step.headers || {}
  const headerEntries = Object.entries(headers)

  // Body shown as JSON text; default placeholder hints "leave empty to send inputs".
  const bodyText = step.body === undefined ? '' :
    typeof step.body === 'string' ? step.body : JSON.stringify(step.body, null, 2)

  return (
    <>
      <div style={TWO_COL}>
        <div>
          <label style={FIELD_LABEL}>Method</label>
          <select
            value={step.method}
            onChange={(e) => upd({ method: e.target.value as HttpMethod })}
            style={INPUT}
          >
            {(['GET', 'POST', 'PUT', 'PATCH', 'DELETE'] as HttpMethod[]).map((m) => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>
        </div>
        <div>
          <label style={FIELD_LABEL}>Save response as</label>
          <input
            value={step.save_response_to || ''}
            onChange={(e) => upd({ save_response_to: e.target.value || undefined })}
            placeholder="e.g. auth → {{auth.user.name}}"
            style={{ ...INPUT, fontFamily: 'monospace' }}
          />
        </div>
      </div>
      <div>
        <label style={FIELD_LABEL}>Save response object to variable</label>
        <input
          value={step.save_response_var || ''}
          onChange={(e) => upd({ save_response_var: e.target.value || undefined })}
          placeholder="page variable — stores the parsed JSON object (e.g. for an embed's recordVar)"
          style={{ ...INPUT, fontFamily: 'monospace' }}
        />
      </div>
      <div>
        <label style={FIELD_LABEL}>Route via IOFlow API</label>
        <select
          value={step.ioflow_api || ''}
          onChange={(e) => upd({ ioflow_api: e.target.value || undefined })}
          style={INPUT}
        >
          <option value="">— direct URL (below) —</option>
          {(ctx.ioflowApis || []).map((name) => (
            <option key={name} value={name}>{name}</option>
          ))}
          {step.ioflow_api && !(ctx.ioflowApis || []).includes(step.ioflow_api) && (
            <option value={step.ioflow_api}>{step.ioflow_api} (not in design)</option>
          )}
        </select>
        {step.ioflow_api && (
          <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', marginTop: 2 }}>
            Sent through IOFlow — the flow subscribed to <code>api.{step.ioflow_api}</code> handles it. URL/headers below are ignored.
          </div>
        )}
      </div>
      <div>
        <label style={FIELD_LABEL}>URL (supports {'{{vars}}'})</label>
        <input
          value={step.url}
          onChange={(e) => upd({ url: e.target.value })}
          placeholder="/api/auth/login OR https://external.example/x"
          style={INPUT}
        />
      </div>
      <div>
        <label style={FIELD_LABEL}>Headers</label>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {headerEntries.map(([k, v], i) => (
            <div key={i} style={{ display: 'flex', gap: 6 }}>
              <input
                value={k}
                onChange={(e) => {
                  const next: Record<string, string> = {}
                  headerEntries.forEach(([kk, vv], j) => { next[j === i ? e.target.value : kk] = vv })
                  upd({ headers: next })
                }}
                placeholder="Header name"
                style={{ ...INPUT, flex: 1 }}
              />
              <input
                value={v}
                onChange={(e) => upd({ headers: { ...headers, [k]: e.target.value } })}
                placeholder="value (supports {{vars}})"
                style={{ ...INPUT, flex: 2 }}
              />
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  const next = { ...headers }; delete next[k]
                  upd({ headers: Object.keys(next).length ? next : undefined })
                }}
                className="btn btn-ghost btn-sm"
                style={{ padding: 4, color: 'var(--color-error)' }}
              ><Trash2 size={13} /></button>
            </div>
          ))}
          <button
            onClick={(e) => { e.stopPropagation(); upd({ headers: { ...headers, '': '' } }) }}
            className="btn btn-ghost btn-sm" style={{ alignSelf: 'flex-start' }}
          ><Plus size={13} /> Add header</button>
        </div>
      </div>
      <div>
        <label style={FIELD_LABEL}>Body (JSON, supports {'{{vars}}'})</label>
        <textarea
          rows={4}
          value={bodyText}
          onChange={(e) => {
            const t = e.target.value
            if (t.trim() === '') { upd({ body: undefined }); return }
            // Try parse — if invalid JSON, keep as string so the user can fix it later.
            try { upd({ body: JSON.parse(t) }) }
            catch { upd({ body: t }) }
          }}
          placeholder="Leave empty to auto-send all page inputs as JSON."
          style={{
            ...INPUT, resize: 'vertical', fontFamily: 'monospace', fontSize: '0.8425rem',
          }}
        />
      </div>

      {/* Recursive branches */}
      <div style={{
        marginTop: 4, paddingTop: 8,
        borderTop: '1px dashed var(--color-border)',
      }}>
        <StepList
          label="On success"
          steps={step.on_success || []}
          onChange={(next) => upd({ on_success: next.length ? next : undefined })}
          ctx={ctx}
        />
      </div>
      <div style={{ paddingTop: 8, borderTop: '1px dashed var(--color-border)' }}>
        <StepList
          label="On error"
          steps={step.on_error || []}
          onChange={(next) => upd({ on_error: next.length ? next : undefined })}
          ctx={ctx}
        />
      </div>
    </>
  )
}

// ─── Multi-name picker (for validate / clear_inputs) ──

function NamesMultiPicker({
  label, value, inputs, onChange,
}: {
  label: string
  value: string[]
  inputs: PageInput[]
  onChange: (next: string[]) => void
}) {
  const toggle = (n: string) => {
    onChange(value.includes(n) ? value.filter((x) => x !== n) : [...value, n])
  }
  return (
    <div>
      <label style={FIELD_LABEL}>{label}</label>
      {inputs.length === 0 ? (
        <div style={{ fontSize: '0.8425rem', color: 'var(--color-text-secondary)' }}>
          No page inputs defined yet.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {inputs.map((i) => (
            <label
              key={i.name}
              style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.8825rem', cursor: 'pointer' }}
            >
              <input type="checkbox" checked={value.includes(i.name)} onChange={() => toggle(i.name)} />
              <span>{i.label || i.name}</span>
              <span style={{ fontFamily: 'monospace', fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>
                ({i.name})
              </span>
            </label>
          ))}
        </div>
      )}
    </div>
  )
}
